import type { AttachKind, PickedFile } from './attachments'
import { WORK_ITEM_LINKS_MAX, workItemLinkProblem } from '@gallop/domain'
import { parseDate } from '@internationalized/date'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, XClose } from '@untitledui/icons'
import { useId, useState } from 'react'
import { Button as AriaButton, Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { Link, useNavigate } from 'react-router'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { InputDate } from '@/components/base/input/input-date'
import { Select } from '@/components/base/select/select'
import { TextArea } from '@/components/base/textarea/textarea'
import { api, ApiError } from '@/shell/api/client'
import { keys, useFileSkills, useTeam } from '@/shell/api/queries'
import { fmtDate } from '@/shell/format'
import { periodLabel } from '@/shell/period'
import { useShell } from '@/shell/store'
import { AttachKindTabs, AttachmentDropZone, fileKey, PageLinkFields, PickedFileList, uploadAttachment } from './attachments'
import { agentAssigneeProblem, disabledSkillKeys, skillSelectHint, skillSelectItems } from './new-work-item-skill'

export type NewItemKind = 'task' | 'request' | 'checklist'

/** PRD-017 §3.11 kinds in the words people use (audit P2-11): a Tax seat's "open item or question" is a request. */
const KIND_LABELS: Record<NewItemKind, string> = { task: 'Task', request: 'Open item or question', checklist: 'Checklist item' }

/**
 * PRD-018 §3.4 title action "Add work item" (file and client pages). Opens a small dialog pre-scoped to the
 * page's client / file and the selector's period: title, kind, assignee (default: you), due date (default:
 * the close target), notes, optional sub-tasks. Create posts through /work-items/quick so the period and
 * due-date defaults are the server's, then opens the drawer on the new item over the Close tab. ⌘K "New
 * task" (PRD-017 §3.13) stays the one-line path; this is the form path.
 */
export function AddWorkItemButton({ clusterId, fileId = null, label = 'Add work item', kind = 'task', testId = 'action-add-item' }: { clusterId: string, fileId?: string | null, label?: string, kind?: NewItemKind, testId?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size="sm" color="secondary" onClick={() => setOpen(true)} data-testid={testId}>{label}</Button>
      {open ? <NewWorkItemDialog clusterId={clusterId} fileId={fileId} initialKind={kind} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

/** Sub-task lines: one per line, blank lines dropped, each trimmed and capped at 300 chars (the API's title limit). */
export function parseSubtaskLines(text: string): string[] {
  return text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => l.slice(0, 300))
}

/** The dialog's assignee select: a person's principal id, or this sentinel for "<file> agent" (QA-29). */
export const AGENT_ASSIGNEE = 'agent'

interface DraftLink { key: string, url: string, label: string }
let linkSeq = 0
const newLink = (): DraftLink => ({ key: `l${++linkSeq}`, url: '', label: '' })

/**
 * QA-29: the link rows that go to the API. A row with an empty URL is dropped (the person added a row and left
 * it); a row with a URL that fails the domain rule is the reason Create stays disabled.
 */
export function prepareLinks(rows: ReadonlyArray<{ url: string, label: string }>): { links: Array<{ url: string, label: string | null }>, problems: Array<{ index: number, problem: string }> } {
  const links: Array<{ url: string, label: string | null }> = []
  const problems: Array<{ index: number, problem: string }> = []
  for (const [index, r] of rows.entries()) {
    if (r.url.trim().length === 0)
      continue
    const problem = workItemLinkProblem({ url: r.url, label: r.label })
    if (problem)
      problems.push({ index, problem })
    else links.push({ url: r.url.trim(), label: r.label.trim() || null })
  }
  return { links, problems }
}

interface Created { id: string, clusterId: string, fileId: string | null, periodId: string | null, period: string, dueOn: string, assigneePrincipalId: string, assigneeKind?: 'person' | 'agent', skillId?: string | null, status?: string, run?: { threadId: string, attemptNo: number, attemptState: string } | { refused: string, message: string } | null }

/** Thrown when the parent was created but a file upload or a sub-task line failed: the dialog reports exactly how far it got (audit P2-16). */
class PartialCreate extends Error {
  constructor(readonly item: Created, readonly what: 'sub-task' | 'file', readonly done: number, readonly total: number, readonly failedTitle: string, readonly reason: string) {
    super('partial')
  }
}

/** The reason line for a failed create step: the API's own message, else the rule, else a plain fallback. */
function refusalReason(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.body.rule === 'attachments.not_configured')
      return 'files are off on this environment'
    return String(e.body.message ?? e.body.rule ?? 'refused')
  }
  return 'network error'
}

/**
 * The form. When `clusterId` is null (quick capture from a page without a client, audit P2-17) the client
 * and file are picked here, from the same tree the shell shows; the defaults the palette described are the
 * initial values, all editable before Create.
 */
export function NewWorkItemDialog({ clusterId: initialCluster, fileId: initialFile, initialKind, initialTitle = '', onClose }: { clusterId: string | null, fileId: string | null, initialKind: NewItemKind, initialTitle?: string, onClose: () => void }) {
  const { bootstrap, period } = useShell()
  const team = useTeam()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const titleId = useId()
  const [clusterId, setClusterId] = useState<string | null>(initialCluster ?? bootstrap.tree[0]?.cluster.id ?? null)
  const [fileId, setFileId] = useState<string | null>(initialFile)
  const [title, setTitle] = useState(initialTitle)
  const [kind, setKind] = useState<NewItemKind>(initialKind)
  const [assignee, setAssignee] = useState(bootstrap.me.principalId)
  // PRD-017 v0.19 §3.11: the skill an agent assignee runs; required, listed from the file's agent with disabled reasons.
  const [skillId, setSkillId] = useState<string | null>(null)
  const [submitProblem, setSubmitProblem] = useState<string | null>(null)
  const [dueOn, setDueOn] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [subtasks, setSubtasks] = useState('')
  const [links, setLinks] = useState<DraftLink[]>([])
  // PRD-017 v0.18: one Attach block, Page first; files are held here and uploaded one by one after Create (the item has no id before).
  const [attachKind, setAttachKind] = useState<AttachKind>('page')
  const [files, setFiles] = useState<PickedFile[]>([])
  const [uploadState, setUploadState] = useState<ReadonlyMap<string, 'uploading' | 'done' | string>>(() => new Map())
  const [error, setError] = useState<string | null>(null)
  const [partial, setPartial] = useState<PartialCreate | null>(null)
  const node = bootstrap.tree.find(t => t.cluster.id === clusterId)
  const fileNode = fileId ? node?.files.find(f => f.id === fileId) ?? null : null
  const scopeName = fileId ? fileNode?.name ?? 'this file' : node?.cluster.name ?? 'this client'
  const people = (team.data?.seats ?? []).filter(s => !s.deactivated_at && s.accepted_at)
  const meListed = people.some(s => s.principal_id === bootstrap.me.principalId)
  const pickScope = initialCluster === null
  const role = bootstrap.me.role
  // PRD-017 §3.11: requests are a CFO / Tax kind; a Tax seat arrives here with it preselected.
  const kinds = (Object.keys(KIND_LABELS) as NewItemKind[]).filter(k => k !== 'request' || role === 'cfo' || role === 'tax' || initialKind === 'request')
  // QA-29 (Sean 2026-09-13 21:59), amended by PRD-017 v0.19 (Sean 2026-09-22, Seb UX review note 34, decision 6): the
  // file's agent is an assignee option and choosing it requires a Skill; Create launches the run. A paper file has no
  // agent (QA-27); a client-level item offers the client's agent only when one exists (none in R1: an agent works on a file).
  const agentOption = fileId
    ? (fileNode && fileNode.books !== 'none' ? { id: AGENT_ASSIGNEE, label: `${fileNode.name} agent`, supportingText: 'runs a skill you pick' } : null)
    : (node?.cluster.agentPrincipalId ? { id: AGENT_ASSIGNEE, label: `${node.cluster.name} agent`, supportingText: 'runs a skill you pick' } : null)
  const assigneeItems = [
    ...(!meListed ? [{ id: bootstrap.me.principalId, label: bootstrap.me.name, supportingText: 'you' }] : []),
    ...people.map(s => ({ id: s.principal_id, label: s.display_name, supportingText: s.principal_id === bootstrap.me.principalId ? 'you' : undefined })),
    ...(agentOption ? [agentOption] : []),
  ]
  const assigneeIsAgent = assignee === AGENT_ASSIGNEE
  const fileSkills = useFileSkills(fileId, assigneeIsAgent)
  const skillOptions = fileSkills.data?.skills ?? null
  const skillItems = skillSelectItems(skillOptions ?? [])
  const agentProblem = agentAssigneeProblem(assigneeIsAgent, skillId, skillOptions)
  const prepared = prepareLinks(links)
  const isRequest = initialKind === 'request'
  const heading = isRequest ? 'Post an open item or question' : 'New work item'

  const create = useMutation({
    mutationFn: async () => {
      if (!clusterId)
        throw new Error('Pick a client first.')
      const body: Record<string, unknown> = { title: title.trim(), kind, period, ...(fileId ? { fileId } : { clusterId }) }
      if (assigneeIsAgent) {
        body.assignTo = 'agent'
        body.skillId = skillId
      }
      else if (assignee && assignee !== bootstrap.me.principalId) {
        body.assigneePrincipalId = assignee
      }
      if (dueOn)
        body.dueOn = dueOn
      if (notes.trim())
        body.body = notes.trim()
      if (prepared.links.length > 0)
        body.links = prepared.links
      const item = await api<Created>('/work-items/quick', { method: 'POST', json: body, idempotencyKey: crypto.randomUUID() })
      const good = files.filter(f => !f.problem)
      const st = new Map<string, 'uploading' | 'done' | string>()
      for (const [i, f] of good.entries()) {
        st.set(fileKey(f.file), 'uploading')
        setUploadState(new Map(st))
        try {
          await uploadAttachment(item.id, f.file)
          st.set(fileKey(f.file), 'done')
          setUploadState(new Map(st))
        }
        catch (e) {
          const reason = refusalReason(e)
          st.set(fileKey(f.file), reason)
          setUploadState(new Map(st))
          throw new PartialCreate(item, 'file', i, good.length, f.file.name, reason)
        }
      }
      const lines = parseSubtaskLines(subtasks)
      for (const [i, line] of lines.entries()) {
        try {
          await api(`/work-items/${item.id}/subtasks`, { method: 'POST', json: { title: line }, idempotencyKey: crypto.randomUUID() })
        }
        catch (e) {
          throw new PartialCreate(item, 'sub-task', i, lines.length, line, refusalReason(e))
        }
      }
      return item
    },
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: keys.bootstrap })
      void qc.invalidateQueries({ queryKey: ['work-items'] })
      void qc.invalidateQueries({ queryKey: ['checklist'] })
      void qc.invalidateQueries({ queryKey: ['attachments'] })
      void qc.invalidateQueries({ queryKey: ['threads'] })
      onClose()
      const base = item.fileId ? `/c/${item.clusterId}/f/${item.fileId}/close` : `/c/${item.clusterId}/close`
      navigate(`${base}/w/${item.id}?period=${item.period}`)
    },
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: ['work-items'] })
      void qc.invalidateQueries({ queryKey: ['checklist'] })
      if (e instanceof PartialCreate)
        setPartial(e)
      else setError(e instanceof ApiError ? String(e.body.message ?? 'Could not create the item.') : e.message || 'Could not create the item. Check your connection and try again.')
    },
  })

  return (
    <ModalOverlay isOpen isDismissable onOpenChange={o => !o && onClose()} className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/40 p-4">
      <Modal className="w-full max-w-md rounded-xl bg-primary p-6 shadow-xl">
        <Dialog aria-labelledby={titleId} className="outline-none" data-testid="new-work-item-dialog">
          <h2 id={titleId} className="text-lg font-semibold text-primary">{heading}</h2>
          <p className="mt-1 text-sm text-tertiary">
            {scopeName}
            {' · '}
            {periodLabel(period)}
          </p>
          {partial
            ? (
                <div role="alert" className="mt-4 rounded-lg border border-warning bg-warning-secondary p-3 text-sm text-warning-primary" data-testid="new-item-partial">
                  <p className="font-medium">{`The item was created with ${partial.done} of ${partial.total} ${partial.what}s.`}</p>
                  <p className="mt-1 text-xs">{`“${partial.failedTitle}” did not go through (${partial.reason}); the ${partial.what === 'file' ? 'files after it, and any sub-tasks,' : 'lines after it'} were not sent. Open the item to add them.`}</p>
                  <div className="mt-2 flex justify-end gap-2">
                    <Button type="button" color="secondary" size="sm" onClick={onClose}>Close</Button>
                    <Link to={`/w/${partial.item.id}`} onClick={onClose} className="inline-flex h-8 items-center rounded-lg bg-brand-solid px-3 text-sm font-semibold text-primary_on-brand hover:bg-brand-solid_hover">Open item</Link>
                  </div>
                </div>
              )
            : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (title.trim().length === 0)
                      return
                    setError(null)
                    // ui-copy-rules §3: validate on submit; an agent with no skill is refused here with the sentence under the select (edge case 96).
                    if (agentProblem) {
                      setSubmitProblem(agentProblem)
                      return
                    }
                    setSubmitProblem(null)
                    create.mutate()
                  }}
                  className="mt-4 flex flex-col gap-3"
                >
                  <Input label="Title" name="title" autoFocus isRequired value={title} onChange={setTitle} placeholder={isRequest ? 'What do you need from the file team?' : 'What needs doing'} data-testid="new-item-title" />
                  {pickScope
                    ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Select
                            label="Client"
                            size="sm"
                            items={bootstrap.tree.map(t => ({ id: t.cluster.id, label: t.cluster.name }))}
                            selectedKey={clusterId}
                            onSelectionChange={(k) => {
                              setClusterId(k === null ? null : String(k))
                              setFileId(null)
                              // The agent option belongs to the file it was offered for; a scope change drops it.
                              setAssignee(a => a === AGENT_ASSIGNEE ? bootstrap.me.principalId : a)
                            }}
                            data-testid="new-item-client"
                          >
                            {item => <Select.Item id={item.id} label={item.label} />}
                          </Select>
                          <Select
                            label="File"
                            size="sm"
                            placeholder="Client level"
                            items={[{ id: '', label: 'Client level' }, ...(node?.files ?? []).map(f => ({ id: f.id, label: f.name }))]}
                            selectedKey={fileId ?? ''}
                            onSelectionChange={(k) => {
                              setFileId(k === null || k === '' ? null : String(k))
                              setAssignee(a => a === AGENT_ASSIGNEE ? bootstrap.me.principalId : a)
                            }}
                            isDisabled={!node}
                            data-testid="new-item-file"
                          >
                            {item => <Select.Item id={item.id} label={item.label} />}
                          </Select>
                        </div>
                      )
                    : null}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Select label="Kind" size="sm" items={kinds.map(k => ({ id: k, label: KIND_LABELS[k] }))} selectedKey={kind} onSelectionChange={k => setKind(String(k) as NewItemKind)} data-testid="new-item-kind">
                      {item => <Select.Item id={item.id} label={item.label} />}
                    </Select>
                    <Select
                      label="Assignee"
                      size="sm"
                      items={assigneeItems}
                      selectedKey={assignee}
                      onSelectionChange={(k) => {
                        setAssignee(String(k))
                        setSubmitProblem(null)
                        if (String(k) !== AGENT_ASSIGNEE)
                          setSkillId(null)
                      }}
                      data-testid="new-item-assignee"
                    >
                      {item => <Select.Item id={item.id} label={item.label} supportingText={item.supportingText} />}
                    </Select>
                  </div>
                  {assigneeIsAgent
                    ? (
                        <Select
                          label="Skill"
                          size="sm"
                          isRequired
                          validationBehavior="aria"
                          placeholder={fileSkills.isPending ? 'Loading…' : 'Pick the skill the agent runs'}
                          items={skillItems}
                          disabledKeys={disabledSkillKeys(skillOptions ?? [])}
                          selectedKey={skillId}
                          onSelectionChange={(k) => {
                            setSkillId(k === null ? null : String(k))
                            setSubmitProblem(null)
                          }}
                          isInvalid={Boolean(submitProblem)}
                          hint={submitProblem ?? skillSelectHint(skillOptions, fileSkills.isPending)}
                          data-testid="new-item-skill"
                        >
                          {item => <Select.Item id={item.id} label={item.label} supportingText={item.supportingText} isDisabled={item.isDisabled} />}
                        </Select>
                      )
                    : null}
                  <InputDate
                    label="Due date"
                    size="sm"
                    value={dueOn ? parseDate(dueOn) : null}
                    onChange={v => setDueOn(v ? v.toString() : null)}
                    hint={dueOn ? `Due ${fmtDate(dueOn, { year: 'always' })}.` : `Leave empty for the close target of ${periodLabel(period)}.`}
                    data-testid="new-item-due"
                  />
                  <TextArea label="Notes" size="sm" value={notes} onChange={setNotes} rows={2} data-testid="new-item-notes" />
                  {/* PRD-017 v0.18: one Attach block, Page (a link) first, File second; both list on the item's Attachments once created. */}
                  <fieldset className="rounded-lg border border-secondary p-3" data-testid="new-item-attachments">
                    <legend className="px-1 text-sm font-medium text-secondary">Attach</legend>
                    <AttachKindTabs value={attachKind} onChange={setAttachKind} testId="new-item-attach" />
                    {attachKind === 'page'
                      ? (
                          <div data-testid="new-item-links">
                            <p className="mt-2 text-xs text-tertiary">Paste a Dropbox share link or any https address for the documents this item needs.</p>
                            {links.length > 0
                              ? (
                                  <ol className="mt-2 flex flex-col gap-2">
                                    {links.map((l, i) => (
                                      <li key={l.key} className="grid grid-cols-[1fr_auto] items-start gap-2" data-testid="new-item-link-row">
                                        <PageLinkFields url={l.url} label={l.label} onUrl={v => setLinks(ls => ls.map((x, j) => j === i ? { ...x, url: v } : x))} onLabel={v => setLinks(ls => ls.map((x, j) => j === i ? { ...x, label: v } : x))} ariaPrefix={`Page ${i + 1}`} testId="new-item-link" />
                                        <AriaButton aria-label={`Remove page ${i + 1}`} onPress={() => setLinks(ls => ls.filter((_, j) => j !== i))} className="mt-1 flex size-7 items-center justify-center rounded-md text-tertiary outline-none hover:bg-secondary hover:text-primary focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]" data-testid="new-item-link-remove">
                                          <XClose className="size-4" />
                                        </AriaButton>
                                      </li>
                                    ))}
                                  </ol>
                                )
                              : null}
                            <Button type="button" size="sm" color="link-gray" className="mt-2" iconLeading={Plus} onClick={() => setLinks(ls => [...ls, newLink()])} isDisabled={links.length >= WORK_ITEM_LINKS_MAX} data-testid="new-item-link-add">Add page</Button>
                          </div>
                        )
                      : (
                          <div className="mt-2 flex flex-col gap-2" data-testid="new-item-files">
                            <AttachmentDropZone label="Attach files" compact onFiles={picked => setFiles(fs => [...fs, ...picked])} testId="new-item-dropzone" />
                            <PickedFileList files={files} onRemove={key => setFiles(fs => fs.filter(f => f.key !== key))} uploading={uploadState} />
                            <p className="text-xs text-quaternary">Files upload once the item is created, one by one, and land as Scanning.</p>
                          </div>
                        )}
                  </fieldset>
                  <details className="rounded-lg border border-secondary p-3" data-testid="new-item-subtasks">
                    <summary className="cursor-pointer text-sm font-medium text-secondary">Sub-tasks (optional)</summary>
                    <TextArea aria-label="Sub-tasks, one per line" size="sm" className="mt-2" value={subtasks} onChange={setSubtasks} rows={3} placeholder="One per line" hint="Each line becomes a sub-task; the agent verification sub-task is added with the first one." />
                  </details>
                  {error ? <p role="alert" className="text-sm text-error-primary">{error}</p> : null}
                  <div className="mt-1 flex justify-end gap-2">
                    <Button type="button" color="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit" isLoading={create.isPending} isDisabled={title.trim().length === 0 || !clusterId || prepared.problems.length > 0} data-testid="new-item-create">Create</Button>
                  </div>
                </form>
              )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
